interface QueuedRequest {
  id: string;
  url: string;
  options: RequestInit;
  timestamp: number;
  retries: number;
}

const TRANSIENT_HTTP_STATUS_CODES = new Set([408, 425, 429, 500, 502, 503, 504]);
const DEFAULT_STORAGE_KEY = 'offline-queue';
const MAX_RETRY_DELAY_MS = 30_000;
const BASE_RETRY_DELAY_MS = 1_000;

function isTransientHttpStatus(status: number): boolean {
  return TRANSIENT_HTTP_STATUS_CODES.has(status);
}

function isNetworkFetchError(error: unknown): boolean {
  if (!(error instanceof TypeError)) {
    return false;
  }

  const message = error.message;
  return (
    message.includes('Failed to fetch') ||
    message.includes('Load failed') ||
    message.includes('NetworkError') ||
    message.includes('Network request failed')
  );
}

function normalizeHeaders(headers?: HeadersInit): Record<string, string> {
  if (!headers) {
    return {};
  }

  if (headers instanceof Headers) {
    const result: Record<string, string> = {};
    headers.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }

  if (Array.isArray(headers)) {
    return Object.fromEntries(headers);
  }

  return { ...headers };
}

export class OfflineQueue {
  private queue: QueuedRequest[] = [];
  private readonly maxRetries: number;
  private processing = false;
  private readonly storageKey: string;
  private retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private retryAttempt = 0;
  private readonly enableListeners: boolean;

  constructor(options?: {
    storageKey?: string;
    maxRetries?: number;
    enableListeners?: boolean;
  }) {
    this.storageKey = options?.storageKey ?? DEFAULT_STORAGE_KEY;
    this.maxRetries = options?.maxRetries ?? 3;
    this.enableListeners = options?.enableListeners ?? true;
    this.loadQueue();

    if (this.enableListeners) {
      this.setupOnlineListener();
      this.scheduleProcessIfNeeded();
    }
  }

  private loadQueue(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        this.queue = JSON.parse(stored);
      }
    } catch (error) {
      console.error('[OfflineQueue] Failed to load queue from localStorage:', error);
      this.queue = [];
    }
  }

  private saveQueue(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.queue));
      window.dispatchEvent(new CustomEvent('offline-queue-updated'));
    } catch (error) {
      console.error('[OfflineQueue] Failed to save queue to localStorage:', error);
    }
  }

  private setupOnlineListener(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('online', () => {
      this.retryAttempt = 0;
      void this.processQueue();
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        void this.processQueue();
      }
    });
  }

  private getRetryDelayMs(): number {
    return Math.min(MAX_RETRY_DELAY_MS, BASE_RETRY_DELAY_MS * 2 ** this.retryAttempt);
  }

  private scheduleProcessIfNeeded(): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (this.queue.length === 0 || !navigator.onLine) {
      this.clearScheduledProcess();
      return;
    }

    if (this.processing || this.retryTimeoutId !== null) {
      return;
    }

    const delay = this.getRetryDelayMs();
    this.retryAttempt += 1;
    this.retryTimeoutId = setTimeout(() => {
      this.retryTimeoutId = null;
      void this.processQueue();
    }, delay);
  }

  private clearScheduledProcess(): void {
    if (this.retryTimeoutId !== null) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }
    this.retryAttempt = 0;
  }

  async add(url: string, options: RequestInit): Promise<Response> {
    if (navigator.onLine) {
      try {
        return await fetch(url, options);
      } catch (error) {
        if (isNetworkFetchError(error)) {
          return this.queueRequest(url, options);
        }
        throw error;
      }
    }

    return this.queueRequest(url, options);
  }

  private queueRequest(url: string, options: RequestInit): Promise<Response> {
    const request: QueuedRequest = {
      id: `${Date.now()}-${Math.random().toString(36).substring(7)}`,
      url,
      options: {
        ...options,
        headers: normalizeHeaders(options.headers),
      },
      timestamp: Date.now(),
      retries: 0,
    };

    this.queue.push(request);
    this.saveQueue();
    this.scheduleProcessIfNeeded();

    return Promise.reject(new Error('Request queued for offline processing'));
  }

  async processQueue(): Promise<void> {
    if (this.processing || !navigator.onLine || this.queue.length === 0) {
      if (this.queue.length > 0 && navigator.onLine && !this.processing) {
        this.scheduleProcessIfNeeded();
      }
      return;
    }

    this.processing = true;
    window.dispatchEvent(new CustomEvent('offline-queue-processing-started'));

    let shouldRetryLater = false;

    while (this.queue.length > 0 && navigator.onLine) {
      const request = this.queue[0];

      try {
        const response = await fetch(request.url, request.options);

        if (response.ok) {
          this.queue.shift();
          this.saveQueue();
          this.retryAttempt = 0;
          console.log(`[OfflineQueue] Successfully processed request: ${request.url}`);
          continue;
        }

        if (isTransientHttpStatus(response.status)) {
          shouldRetryLater = true;
          break;
        }

        request.retries += 1;
        if (request.retries >= this.maxRetries) {
          console.error('[OfflineQueue] Request failed after max retries:', request);
          this.queue.shift();
          this.saveQueue();
        } else {
          shouldRetryLater = true;
          break;
        }
      } catch (error) {
        if (isNetworkFetchError(error)) {
          shouldRetryLater = true;
          break;
        }

        request.retries += 1;
        if (request.retries >= this.maxRetries) {
          console.error('[OfflineQueue] Request failed after max retries:', request, error);
          this.queue.shift();
          this.saveQueue();
        } else {
          shouldRetryLater = true;
          break;
        }
      }
    }

    this.processing = false;
    window.dispatchEvent(new CustomEvent('offline-queue-processing-finished'));

    if (this.queue.length === 0) {
      this.clearScheduledProcess();
      return;
    }

    if (shouldRetryLater || navigator.onLine) {
      this.scheduleProcessIfNeeded();
    }
  }

  getQueueLength(): number {
    return this.queue.length;
  }

  getQueue(): QueuedRequest[] {
    return [...this.queue];
  }

  clearQueue(): void {
    this.queue = [];
    this.clearScheduledProcess();
    this.saveQueue();
  }

  removeRequest(id: string): void {
    this.queue = this.queue.filter((req) => req.id !== id);
    this.saveQueue();
    if (this.queue.length === 0) {
      this.clearScheduledProcess();
    }
  }
}

export const offlineQueue = new OfflineQueue();
